import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "@/lib/i18n";
import { api } from "@/lib/api";

const AppContext = createContext(null);
const CART_VERSION = 2; // bump when cart shape changes

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

const readCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("zayd_cart") || "{}");
    if (raw && raw.version === CART_VERSION && Array.isArray(raw.items)) return raw.items;
  } catch (e) {
    console.warn("[cart] corrupted localStorage, resetting", e);
  }
  localStorage.removeItem("zayd_cart");
  return [];
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("zayd_lang") || "ar");
  const [theme, setTheme] = useState(() => localStorage.getItem("zayd_theme") || "light");
  const [cart, setCart] = useState(readCart);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("zayd_user") || "null");
    } catch {
      return null;
    }
  });

  const t = useMemo(() => translations[lang] || translations.en, [lang]);

  useEffect(() => {
    document.documentElement.dir = t.dir;
    document.documentElement.lang = lang;
    localStorage.setItem("zayd_lang", lang);
  }, [lang, t.dir]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("zayd_theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("zayd_cart", JSON.stringify({ version: CART_VERSION, items: cart }));
  }, [cart]);

  useEffect(() => {
    if (user) localStorage.setItem("zayd_user", JSON.stringify(user));
    else localStorage.removeItem("zayd_user");
  }, [user]);

  const toggleLang = useCallback(() => setLang((l) => (l === "ar" ? "en" : "ar")), []);
  const toggleTheme = useCallback(() => setTheme((th) => (th === "dark" ? "light" : "dark")), []);

  const cartKey = (productId, variantId, serviceId) =>
    `${productId}::${variantId || ""}::${serviceId || ""}`;

  const setItemInfo = useCallback((productId, variantId, serviceId, info) => {
    setCart((curr) =>
      curr.map((c) =>
        cartKey(c.product.id, c.variant_id, c.service_id) === cartKey(productId, variantId, serviceId)
          ? { ...c, customer_info: info }
          : c
      )
    );
  }, []);

  const lineUnitPrice = useCallback((item) => {
    const base = Number(item.product.base_price || 0);
    const variant = (item.product.variants || []).find((v) => v.id === item.variant_id);
    const service = (item.product.service_options || []).find((s) => s.id === item.service_id);
    return base + Number(variant?.price_modifier || 0) + Number(service?.price_addon || 0);
  }, []);

  const addToCart = useCallback((product, variantId, serviceId, qty = 1, customerInfo = "") => {
    setCart((curr) => {
      const key = cartKey(product.id, variantId, serviceId);
      const idx = curr.findIndex(
        (c) => cartKey(c.product.id, c.variant_id, c.service_id) === key
      );
      if (idx >= 0) {
        const next = [...curr];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty, customer_info: customerInfo || next[idx].customer_info };
        return next;
      }
      return [
        ...curr,
        { product, variant_id: variantId, service_id: serviceId, qty, customer_info: customerInfo },
      ];
    });
  }, []);

  const removeFromCart = useCallback((productId, variantId, serviceId) => {
    setCart((curr) =>
      curr.filter(
        (c) => cartKey(c.product.id, c.variant_id, c.service_id) !== cartKey(productId, variantId, serviceId)
      )
    );
  }, []);

  const updateQty = useCallback((productId, variantId, serviceId, qty) => {
    setCart((curr) =>
      curr
        .map((c) =>
          cartKey(c.product.id, c.variant_id, c.service_id) === cartKey(productId, variantId, serviceId)
            ? { ...c, qty: Math.max(1, qty) }
            : c
        )
        .filter((c) => c.qty > 0)
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = useMemo(() => cart.reduce((s, c) => s + c.qty, 0), [cart]);
  const cartSubtotal = useMemo(
    () => cart.reduce((s, c) => s + lineUnitPrice(c) * c.qty, 0),
    [cart, lineUnitPrice]
  );
  const cartVat = useMemo(() => cartSubtotal * 0.15, [cartSubtotal]);
  const cartTotal = useMemo(() => cartSubtotal + cartVat, [cartSubtotal, cartVat]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("zayd_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const otpRequest = useCallback(async (phone) => {
    const { data } = await api.post("/auth/otp/request", { phone });
    return data;
  }, []);

  const otpVerify = useCallback(async (phone, code, name = "") => {
    const { data } = await api.post("/auth/otp/verify", { phone, code, name });
    localStorage.setItem("zayd_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("zayd_token");
    setUser(null);
  }, []);

  const value = {
    lang,
    setLang,
    toggleLang,
    t,
    theme,
    toggleTheme,
    cart,
    addToCart,
    removeFromCart,
    updateQty,
    setItemInfo,
    clearCart,
    cartCount,
    cartSubtotal,
    cartVat,
    cartTotal,
    lineUnitPrice,
    user,
    login,
    otpRequest,
    otpVerify,
    logout,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
